import { isNotNull, LessonProblem, Problem } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { KeyboardContextProvider, Robot } from 'platform-components';
import { SCREEN_RESOLUTIONS } from 'platform-components/constants';
import { isRobotProblem } from 'platform-components/src/components/robot/utils/isRobotProblem';
import React from 'react';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import AdaptiveCarousel from 'common.components/adaptive-carousel/adaptive-carousel';
import { ProblemWithLabView } from 'common.components/lab-problem/lab-problem';
import { CARD_BADGES } from 'common.components/stack/constants';

import './stack__variants.scss';

export interface StackVariantsProps {
    cardBadges?: Record<string, CARD_BADGES>;
    disableProblemsInLesson: boolean;
    isMobile: boolean;
    mix?: string;
    onCarouselRef?: (ref: HTMLDivElement | null) => void;
    onSelectProblem: ({ id }: { id: number }) => void;
    stack: { problems: LessonProblem[]; id?: number };
    selectedProblemId?: number;
}

export class StackVariants extends React.Component<StackVariantsProps> {
    renderProblems = (problems: Problem[]): Array<React.ReactElement> | null => {
        const { cardBadges = {}, disableProblemsInLesson, onSelectProblem, selectedProblemId } = this.props;

        if (problems.length <= 1) {
            return null;
        }

        return problems.map((problem) => {
            const id = problem.id;
            const selected = id === selectedProblemId;

            const disabled = disableProblemsInLesson
                ? cardBadges[id] === CARD_BADGES.USED_IN_LESSON && !selected
                : false;

            return (
                <div
                    className={b('stack', 'problems-preview', {
                        disabled,
                        selected,
                        badge: cardBadges[id],
                    })}
                    key={id}
                    {...accessibleClick(disabled ? undefined : () => onSelectProblem({ id }))}
                >
                    <div className={b('stack', 'problem-preview')}>
                        {isRobotProblem(problem) ? (
                            <Robot previewMode problem={problem} />
                        ) : (
                            <ProblemWithLabView
                                imageResolution={SCREEN_RESOLUTIONS.X1}
                                isFilled
                                previewMode={true}
                                problem={problem}
                            />
                        )}
                    </div>
                </div>
            );
        });
    };

    getProblems = () => {
        const { problems: problemLinks } = this.props.stack;

        return problemLinks.map(({ problem }) => problem).filter<Problem>(isNotNull);
    };

    render() {
        const { mix, isMobile, onCarouselRef, selectedProblemId } = this.props;

        const problems = this.getProblems();

        if (problems.length <= 1) {
            return null;
        }

        const selectedProblemIndex = problems.findIndex((problem) => problem.id === selectedProblemId);

        return (
            <div className={cx(b('stack', 'carousel', { mobile: isMobile }), mix)} ref={onCarouselRef}>
                <KeyboardContextProvider>
                    <AdaptiveCarousel initialItemIndex={selectedProblemIndex} isMobile={isMobile} withControls={true}>
                        {this.renderProblems(problems)}
                    </AdaptiveCarousel>
                </KeyboardContextProvider>
            </div>
        );
    }
}

export default StackVariants;
